import { useCallback, useState } from 'react';
import { LenderFieldMap } from '@trnsact/trnsact-shared-types';
import _ from 'lodash';

interface Options {
  dynamicLenderMappings: LenderFieldMap[];
  submitCreditApp: (sharedDocuments: any) => void;
  handleClose: () => void;
  sharedDocuments: any;
}

export function useDlmSubmission({ dynamicLenderMappings, submitCreditApp, handleClose, sharedDocuments }: Options) {
  const [openDlmErrorSnackbar, setOpenDlmErrorSnackbar] = useState(false);
  const [dlmErrorSnackbarMessage, setDlmErrorSnackbarMessage] = useState('');
  const [submittingApp, setSubmittingApp] = useState(false);

  const checkAllRequiredFieldsHaveValues = useCallback(() => {
    let allRequiredFieldsHaveValue = true;
    dynamicLenderMappings.forEach(field => {
      if (
        (field.details?.fieldInputType === 'OPTION_SET'
          ? !field.details?.optionSets?.find((os: any) => os.lenderValue === field.mappedValue)
          : !field.mappedValue) &&
        field.mappedValue !== 0 &&
        field.details?.required === 'REQUIRED' &&
        !field.details?.hidden
      ) {
        allRequiredFieldsHaveValue = false;
      }
    });
    return allRequiredFieldsHaveValue;
  }, [dynamicLenderMappings]);

  const getFieldsWithErrors = useCallback(() => {
    const fieldsWithErrors: LenderFieldMap[] = [];
    dynamicLenderMappings.forEach(field => {
      try {
        const errors = (field.details as any)?.errors;
        if (errors && errors.length > 0 && field.details?.hidden === false) {
          fieldsWithErrors.push(field);
        }
      } catch (e: any) {
        console.log(`Error checking if field has errors: ${JSON.stringify(field)}: ${e.message}`);
      }
    });
    return fieldsWithErrors;
  }, [dynamicLenderMappings]);

  const removeMasks = useCallback(() => {
    dynamicLenderMappings.forEach((field, i) => {
      try {
        if (dynamicLenderMappings[i].mappedValue) {
          if (field.details?.fieldInputType === 'PHONE_NUMBER') {
            dynamicLenderMappings[i].mappedValue = String(dynamicLenderMappings[i].mappedValue).replace(
              /[()\-\s]/g,
              ''
            );
          } else if (field.details?.fieldInputType === 'SSN' || field.details?.fieldInputType === 'FEIN') {
            dynamicLenderMappings[i].mappedValue = String(dynamicLenderMappings[i].mappedValue).replace(/-/g, '');
          }
        }
      } catch (e: any) {
        console.log(`Error trying to remove mask: ${e.message}`);
      }
    });
  }, [dynamicLenderMappings]);

  const handleDlmFormSubmit = useCallback(() => {
    const fieldsWithErrors = getFieldsWithErrors();
    const allRequiredFieldsHaveValue = checkAllRequiredFieldsHaveValues();

    if (allRequiredFieldsHaveValue && fieldsWithErrors.length === 0) {
      removeMasks();
      submitCreditApp(sharedDocuments);
      setSubmittingApp(true);
      handleClose();
      setOpenDlmErrorSnackbar(false);
      setDlmErrorSnackbarMessage('');
    } else {
      const message = `Please: ${allRequiredFieldsHaveValue ? '' : 'Fill out all required fields.'} ${
        fieldsWithErrors.length > 0
          ? `Fix the following fields: ${fieldsWithErrors
              .map(f => `${f.details?.category ?? ''} ${f.details?.displayName}`)
              .join(', ')}`
          : ''
      }`;
      setDlmErrorSnackbarMessage(message);
      setOpenDlmErrorSnackbar(true);
    }
  }, [
    checkAllRequiredFieldsHaveValues,
    getFieldsWithErrors,
    removeMasks,
    submitCreditApp,
    handleClose,
    sharedDocuments,
  ]);

  return {
    submittingApp,
    openDlmErrorSnackbar,
    dlmErrorSnackbarMessage,
    handleDlmFormSubmit,
    checkAllRequiredFieldsHaveValues,
    setOpenDlmErrorSnackbar,
  };
}
